/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { APIEnum } from '../../../modules/loadbalancer/shared';
import { AuthStoreModel } from '../../../modules/loadbalancer/shared/models/authStore.model';
import { ErrorResponseModel } from '../../models';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { LoadingService, LogService, NotifyService, UtilsService } from '../index';
import { ModulesEnum, TokenStatus } from '../../enums';
import { Observable, of, throwError as observableThrowError } from 'rxjs';
import { Router } from '@angular/router';
import { UserService } from './user.service';
import { ValidationService } from '../component/validation/validation.service';
import { catchError, map, share, timeout } from 'rxjs/operators';

/**
 * Notes: Look into using the _log service for logging.
 *
 * Available request options:
 * skip: boolean = Send the request without the queue.
 * attempts: number = Total number of times to attempt a request.
 * timeout: number = The amount of time before a request timeout.
 *
 * Transaction System:
 * start - init the transaction process.
 * commit - finalise pending changes.
 * rollback - revert the pending changes.
 */

@Injectable()
export class ApiService {
    /**
     * The pending requests stored in an array.
     * @private
     */
    private requestQueue: any[] = [];

    /**
     * An array to quickly find requests based off their url and method.
     * @private
     */
    private requestMap: any[] = [];

    /**
     * HTTP Request Method ENUM.
     * @private
     */
    private method = {
        GET: 'get',
        POST: 'post',
        DELETE: 'delete',
        PUT: 'put',
        PATCH: 'patch'
    };

    constructor(
        private http: HttpClient,
        private loadingService: LoadingService,
        private logService: LogService,
        private notifyService: NotifyService,
        private router: Router,
        private userService: UserService,
        private utilsService: UtilsService,
        private validationService: ValidationService
    ) {
        this.userService.logOutEmitter
            .subscribe({
                next: () => {
                    this.requestQueue = [];
                }
            });
    }

    /**
     * Send ping request to host.
     * @param url
     * @param module
     * @param tLimit
     */
    public ping(url: string, module: ModulesEnum, tLimit: number = 10000) {
        return new Observable(
            (observer) => {
                this.logService.debug('ApiService:ping()', `Pinging module ${module} with limit ${tLimit}`, url);
                observer.next(true);
                observer.complete();
            }
        );
    }

    /**
     * Perform an HTTP GET request.
     * @param auth
     * @param url
     * @param applianceIdRequired
     * @param options
     */
    public get(url: string, auth: AuthStoreModel, applianceIdRequired: boolean = false, options: any = {}) {
        this.logService.debug('ApiService:get()', 'Queueing GET request', url);
        if (options.skip === undefined) {
            options.skip = true;
        }

        return this.queue(this.method.GET, url, null, auth, applianceIdRequired, options);
    }

    /**
     * Perform an HTTP POST request.
     * @param url
     * @param auth
     * @param data
     * @param applianceIdRequired
     * @param options
     */
    public post(url: string, data: any, auth: AuthStoreModel, applianceIdRequired: boolean = false, options: any = {}) {
        this.logService.debug('ApiService:post()', 'Queueing POST request', url);

        return this.queue(this.method.POST, url, data, auth, applianceIdRequired, options);
    }

    /**
     * Perform an HTTP DELETE request.
     * @param url
     * @param auth
     * @param applianceIdRequired
     * @param options
     */
    public delete(url: string, auth: AuthStoreModel, applianceIdRequired: boolean = false, options: any = {}) {
        this.logService.debug('ApiService:delete()', 'Queueing DELETE request', url);

        return this.queue(this.method.DELETE, url, null, auth, applianceIdRequired, options);
    }

    /**
     * Perform an HTTP PUT request.
     * @param url
     * @param auth
     * @param data
     * @param applianceIdRequired
     * @param options
     */
    public put(url: string, data: any, auth: AuthStoreModel, applianceIdRequired: boolean = false, options: any = {}) {
        this.logService.debug('ApiService:put()', 'Queueing PUT request', url);

        return this.queue(this.method.PUT, url, data, auth, applianceIdRequired, options);
    }

    /**
     * Perform an HTTP PATCH request.
     * @param url
     * @param auth
     * @param data
     * @param applianceIdRequired
     * @param options
     */
    public patch(url: string, data: any, auth: AuthStoreModel, applianceIdRequired: boolean = false, options: any = {}) {
        this.logService.debug('ApiService:patch()', 'Queueing PATCH request', url);

        return this.queue(this.method.PATCH, url, data, auth, applianceIdRequired, options);
    }

    /**
     * Handle request errors.
     * Use this function if you are using a forkJoin and want the request to complete even if one part fails.
     * @param errorList
     * @param callName
     */
    public handleRequest(errorList: any, callName?: string): any {
        return (observable: Observable<any>) => {
            return observable.pipe(
                map((result) => result),
                catchError((err) => {
                    const date = new Date();
                    errorList[callName] = { date, err };

                    return of(null);
                })
            );
        };
    }

    /**
     * TODO: Notes: Perhaps we should ping the host before queueing?
     * We are probably making multiple requests each time processQueue is called
     * @param method
     * @param url
     * @param data
     * @param auth
     * @param applianceIdRequired
     * @param options
     * @private
     */
    private queue(method: string, url: string, data: any, auth: AuthStoreModel, applianceIdRequired: boolean, options: any = {}) {
        let request = this.isDuplicateRequest(method + url, data);
        if (!request) {
            const id = this.utilsService.generateRandomString();
            const applianceId = applianceIdRequired ? this.getApplianceId(options) : applianceIdRequired;
            const requestStruct = this.buildRequestStruct(method, url, data, applianceId, request, options, id, auth);
            this.queueAddItem(id, requestStruct);
            request = new Observable(
                (observer) => {
                    this.queueSetObserver(id, observer);
                    this.createRequest(request, options, method, url, id, requestStruct);
                }
            ).pipe(
                share()
            );
        }

        return request;
    }

    /**
     * Build the request structure and return the object.
     * @param method
     * @param url
     * @param data
     * @param applianceId
     * @param request
     * @param options
     * @param id
     * @param auth
     * @private
     */
    private buildRequestStruct(
        method: string,
        url: string,
        data: any,
        applianceId: any,
        request: any,
        options: {
            attempts: any;
            timeout: any;
            noNotification: boolean
        },
        id: string,
        auth: AuthStoreModel
    ) {
        return {
            method,
            url,
            data,
            applianceId,
            request,
            counter: 0,
            attempts: options.attempts || 3,
            blocked: false,
            timeout: options.timeout || 10000,
            noNotification: options.noNotification || false,
            id,
            auth
        };
    }

    /**
     * Return the appliance Id.
     * @param options
     * @private
     */
    private getApplianceId(options: { applianceId: any; }) {
        return options?.applianceId ? options.applianceId : this.userService.getUser('applianceId');
    }

    /**
     * Create the request.
     * @param request
     * @param options
     * @param method
     * @param url
     * @param id
     * @param requestStruct
     * @private
     */
    private createRequest(request: any, options: any, method: string, url: string, id: string, requestStruct: any) {
        // Are we queueing this item or sending it as async?
        if (options.skip) {
            this.logService.debug(
                'ApiService:queue()', method + ' request options SKIP included. Skipping queue.', url
            );
            this.refreshTokenAndSendRequest(requestStruct);

            return request;
        }

        this.mapAddItem(method + url, id);
        this.processQueue();
    }

    /**
     * Loop through request to find out if it is a duplicate.
     * @param urlMethod
     * @param data
     * @private
     */
    private isDuplicateRequest(urlMethod: string, data: any): any {
        if (data !== null && this.mapItemExist(urlMethod)) {
            for (const i in this.requestMap) {
                if (this.requestMap[i]) {
                    const item = this.requestMap[i][i];
                    for (const j in item[1]) {
                        if (item[j]) {
                            for (const k in this.requestQueue) {
                                if (this.requestQueue[k]) {
                                    const request = this.requestQueue[k][i];
                                    if (request[0] === item[j] && request[1].data === data) {
                                        this.logService.debug('ApiService:isDuplicateRequest()',
                                            'Duplicate request found!! Skipping');

                                        return request[1];
                                    }
                                }
                            }
                        }
                    }
                }
            }
            this.logService.debug('ApiService:isDuplicateRequest()',
                'Duplicate request found!! but with different data.');
        }

        return false;
    }

    /**
     * Get a queue item by id.
     * @param id
     * @private
     */
    private queueGetObserver(id: string) {
        for (const i in this.requestQueue) {
            if (this.requestQueue[i]) {
                if (this.requestQueue[i][0] && this.requestQueue[i][0] === id) {
                    return this.requestQueue[i][2];
                }
            }
        }

        return false;
    }

    /**
     * Set the request observer.
     * @param id
     * @param observer
     * @private
     */
    private queueSetObserver(id: string, observer: any) {
        for (const i in this.requestQueue) {
            if (this.requestQueue[i]) {
                if (this.requestQueue[i][0] && this.requestQueue[i][0] === id) {
                    return this.requestQueue[i][2] = observer;
                }
            }
        }
    }

    /**
     * Add a queue item.
     * @param id
     * @param request
     * @param observer
     * @private
     */
    private queueAddItem(id: string, request: any, observer = null) {
        for (const i in this.requestQueue) {
            if (this.requestQueue[i]) {
                if (this.requestQueue[i][0] && this.requestQueue[i][0] === id) {
                    this.requestQueue[1].push(id);

                    return;
                }
            }
        }
        this.requestQueue.push([id, request, observer]);
    }

    /**
     * Remove a queued item.
     * @param id
     * @private
     */
    private queueRemoveItem(id: string) {
        let counter = 0;
        for (const i in this.requestQueue) {
            if (this.requestQueue[i]) {
                if (this.requestQueue[i][0] && this.requestQueue[i][0] === id) {
                    this.requestQueue.splice(counter, 1);

                    return;
                }
                counter++;
            }
        }
    }

    /**
     * Check if an item in the requestMap exists
     * @param urlMethod (method + url)
     * @param id uid for the request
     * @private
     */
    private mapItemExist(urlMethod: string, id: string = null): boolean {
        for (const i in this.requestMap) {
            if (this.requestMap[i]) {
                if (id === null) {
                    if (this.requestMap[i][0] && this.requestMap[i][0] === urlMethod) {
                        return true;
                    }
                } else {
                    if (this.requestMap[i][0] === urlMethod) {
                        return this.requestMap[i][1].indexOf(id) > -1;
                    }
                }
            }
        }

        return false;
    }

    /**
     * Add an item to our request map.
     * @param urlMethod
     * @param id
     */
    private mapAddItem(urlMethod: string, id: string) {
        if (this.requestMap?.length) {
            for (const i in this.requestMap) {
                if (this.requestMap[i]) {
                    if (this.requestMap[i][0] && this.requestMap[i][0] === urlMethod) {
                        this.requestMap[i][1].push(id);

                        return;
                    }
                }
            }
            this.requestMap.push([urlMethod, [id]]);
        }
    }

    /**
     * Remove an item from our request map. If the map is then empty, delete the entry in the map all together.
     * @param urlMethod
     * @param id
     */
    private mapRemoveItem(urlMethod: any, id: string) {
        if (this.requestMap?.length) {
            let counter = 0;
            for (const i in this.requestMap) {
                if (this.requestMap[i]) {
                    if (this.requestMap[i][0] && this.requestMap[i][0] === urlMethod) {
                        const totalIDs = this.requestMap[i][1].length;
                        if (totalIDs > 1) {
                            const idIndex = this.requestMap[i][1].indexOf(id);

                            this.requestMap[i][1].splice(idIndex, 1);
                        } else {
                            this.requestMap.splice(counter, 1);
                        }

                        return;
                    }
                    counter++;
                }
            }
        }
    }

    /**
     * Process request queue.
     * @private
     */
    private processQueue() {
        if (this.requestQueue?.length) {
            const request = this.requestQueue[0][1];
            this.logService.debug(
                'ApiService:processQueue()',
                'Queue Total',
                { total: this.requestQueue.length }
            );
            this.logService.debug(
                'ApiService:processQueue()',
                'Sending request',
                { id: this.requestQueue[0][0] }
            );
            this.refreshTokenAndSendRequest(request);
        }
    }

    /**
     * Refresh the Token and if OK send the request; logout if expired;
     * @param request
     * @private
     */
    private refreshTokenAndSendRequest(request: any) {
        const tokenStatus: TokenStatus = this.userService.tokenStatus();
        if (tokenStatus === TokenStatus.EXPIRED) {
            this.userService.logout();

            return;
        }

        if (request.url !== APIEnum.GET_TOKEN && tokenStatus === TokenStatus.OK) {
            this.sendRequest(request);

            return;
        }
        // Make request to refresh our token (do not use API service as it will be added to the end of queue and not called)
        const localUser = this.userService.getUser();

        const url = 'https://' + localUser.applianceAddress + ':'
            + localUser.appliancePort + '/api/' + APIEnum.VERSION + '/token/';
        this.http.get(url)
            .subscribe({
                next: (response: any): void => {
                    this.refreshUserToken(response.data, localUser, request);
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.debug('ApiService:refreshToken()', 'Request failed.');
                    if (this.validationService.isJson(error).error) {
                        this.logService.debug('ApiService:refreshTokenAndSendRequest()',
                            'Invalid JSON response from host.'
                        );
                    }
                    this.userService.logout();
                }
            });
    }

    /**
     * Refresh the user token; update local storage token and continue with request.
     * @param data
     * @param localUser
     * @param request
     * @private
     */
    private refreshUserToken(data: any, localUser: any, request: any) {
        localUser.JWT = data.token;
        localUser.timestampJWT = Math.floor(Date.now() / 1000);
        this.logService.debug(
            'ApiService:refreshToken()', 'Successfully renewed token',
            localUser
        );
        this.userService.updateUserLocalStorage();
        this.sendRequest(request);
    }

    /** Helper function to improve readability.
     * @param response
     * @param message
     * @param notificationId
     * @private
     */
    private handleLocalNodeErrors(response: { status: string; }, message: string, notificationId: string): void {
        this.notifyService.showNotification(response.status, this.utilsService.firstLetterUppercase(message), notificationId);
    }

    /**
     * To show notifications on nodes with errors.
     * @param nodes
     * @private
     */
    private handleNodeErrors(nodes: any) {
        if (nodes) {
            for (const i in nodes) {
                if (nodes[i]) {
                    this.notifyService.showNotification(nodes[i].status, this.utilsService.firstLetterUppercase(nodes[i].message));
                }
            }
        }

    }

    /**
     * Send an HTTP request.
     * @param request
     * @private
     */
    private sendRequest(request: any) {
        if (request.blocked) {
            return;
        } else {
            request.blocked = true;
        }

        const notificationId: string = this.utilsService.convertStringToId(`${request.url}-${request.method}`);
        if (request.method !== 'GET') {
            this.notifyService.removeNotificationById(notificationId);
        }

        const httpRequest = this.createHttpRequest(request);
        httpRequest.subscribe({
            next: (response: any): void => {
                const message = this.getMessage(response);
                response['uId'] = this.utilsService.getResponseUId(response, request.url).toString();
                // Handle success message automagically.
                if (response.status && message && request.method !== 'get') {
                    this.handleLocalNodeErrors(response, message, notificationId);
                    this.handleNodeErrors(response['data'].nodes);
                }

                this.handleFailOrErrorResponse(response, notificationId, request);
                this.queueGetObserver(request.id).next(response.data);
                this.queueGetObserver(request.id).complete();
                this.cleanUpRequest(request);

                this.processQueue();
            },
            error: (errorResponse: any): void => {
                errorResponse.error['apiStatus'] = errorResponse.status;
                errorResponse['uId'] = this.utilsService.getResponseUId(errorResponse.error, request.url).toString();
                this.processHttpError(errorResponse.error, notificationId, request);
            }
        });
    }

    /**
     * Get message from response.
     * @param response
     * @private
     */
    private getMessage(response: any): string {
        return response.message ? response.message : response.status;
    }

    /**
     * Process error status.
     * @param errorResponse
     * @param request
     * @private
     */
    private processErrorStatus(errorResponse: any, request: any): void {
        const status = `${errorResponse.error.status}`;
        const apiStatus: any = errorResponse.status;
        switch (status) {
            case '401': // Unauthorized Request
                this.handleUnauthorisedRequest(request, apiStatus, errorResponse);
                break;
            case '423': // Server resourced locked.
                this.handleServerResourceLocked(request);
                break;
            default:
                this.queueGetObserver(request.id).error(errorResponse.data);
                this.queueGetObserver(request.id).complete();
                this.cleanUpRequest(request);
                break;
        }
    }

    /**
     * Handle server resource locked state (423).
     * @param request
     * @private
     */
    private handleServerResourceLocked(request: any): void {
        this.logService.debug('ApiService:sendRequest()', 'API Locked will attempt retry..');
        // Check to see if we have reached our request limit
        if (request.counter >= request.attempts) {
            this.logService.debug('ApiService:sendRequest()',
                'Maximum number of attempts reached. Request dropped from queue.');
            this.queueGetObserver(request.id).complete();
            this.cleanUpRequest(request);
        } else {
            // Reset the block so that it can be attempted again
            request.blocked = false;
            // Increment our attempts
            if (request.counter < request.attempts) {
                request.counter++;
            }
        }
    }

    /**
     * Handle unauthorised request (401); check if JWT has expired.
     * @param request
     * @param apiStatus
     * @param errorResponse
     * @private
     */
    private handleUnauthorisedRequest(request: any, apiStatus: string, errorResponse: any): void {
        this.logService.debug('ApiService:sendRequest()', 'Unauthorized request (' + apiStatus + ')');
        // JWT expired
        if (errorResponse.data != null && errorResponse.data.error === 'JWT-expired') {
            this.logService.debug('ApiService:sendRequest()', 'JWT-expired');
        }
        this.router.navigate(['unauthorized']).then().catch();
        this.queueGetObserver(request.id).complete();
        this.cleanUpRequest(request);
    }

    /**
     * Process http error response.
     * @param errorResponse
     * @param notificationId
     * @param request
     * @private
     */
    private processHttpError(errorResponse: any, notificationId: string, request: any): void {
        this.handleFailOrErrorResponse(errorResponse, notificationId, request);

        this.logService.debug('ApiService:sendRequest()', 'Request failed.', {
            id: request.id,
            url: request.url
        });

        if (!(errorResponse.error instanceof Object)) {
            this.logService.debug('ApiService:sendRequest()', 'Invalid JSON response from host.', {
                id: request.id,
                url: request.url,
                data: errorResponse.error
            });

            this.queueGetObserver(request.id).complete();
            this.cleanUpRequest(request);
            this.processQueue();

            return;
        }

        this.processErrorStatus(errorResponse, request);
        this.processQueue();
    }

    /**
     * Create HTTP request.
     * @param request
     * @private
     */
    private createHttpRequest(request: any) {
        const FQUrl = this.buildFQUrl(request.url, request.auth, request.applianceId);

        if (request.method === 'post' || request.method === 'put' || request.method === 'patch') {
            return this.http[request.method](FQUrl, request.data)
                .pipe(
                    timeout(request.timeout),
                    catchError(
                        errorResponse => {
                            return observableThrowError(() => errorResponse);
                        }
                    )
                );
        } else {
            return this.http[request.method](FQUrl)
                .pipe(
                    timeout(request.timeout),
                    catchError(
                        errorResponse => {
                            return observableThrowError(() => errorResponse);
                        }
                    )
                );
        }
    }

    /**
     * Handle fail or error response.
     * @param errorResponse
     * @param id
     * @param request
     * @private
     */
    private handleFailOrErrorResponse(errorResponse: any, id: string, request: any): void {
        errorResponse['uId'] = this.utilsService.getResponseUId(errorResponse, errorResponse.message).toString();
        let message: string = null;

        if (errorResponse.status >= 500 || errorResponse.apiStatus >= 500) {
            //  TODO removed while the backend sort out what is 500 and what is not
            // this.redirectToErrorPage();
            message = this.getMessageFromError(errorResponse);
        } else if (errorResponse.status === 400) {
            //  TODO removed while the backend sort out what is 400 and what is not
            // ||  errorResponse.apiStatus === 400
            message = this.getMessageFromError(errorResponse);
        } else if (
            ([403, 404].includes(errorResponse.status) || [403, 404].includes(errorResponse.apiStatus)) ||
            (['warning', 'fail', 'error'].includes(errorResponse.status) || errorResponse.name === 'TimeoutError')
        ) {
            message = this.getMessageFromError(errorResponse);
        } else if (errorResponse.status === 429) {
            //  Rate limit exceeded
            // this.redirectToErrorPage();
            message = this.getMessageFromError(errorResponse);
        } else if (errorResponse.status === 400 || errorResponse.apiStatus === 400) {
            this.redirectToErrorPage();
        }

        if (message?.length) {
            const sentenceMessage: string = this.utilsService.firstLetterUppercase(message);
            const notificationType: string = this.getNotificationType(errorResponse);
            if (!request.noNotification) {
                this.notifyService.showNotification(notificationType, sentenceMessage, id, errorResponse['uId']);
            }

            this.queueGetObserver(request.id).error(errorResponse);
        }
    }

    /**
     * redirect to error page.
     * @private
     */
    private redirectToErrorPage(): void {
        const excludedPages: string[] = ['/', '/login', '/cm/dashboard', '/403', '/404'];
        const routeIsErrorPage: boolean = this.router.url.includes('page-error');
        const routeIsOverview: boolean = this.router.url.includes('overview');
        if (!excludedPages.includes(this.router.url) && !routeIsErrorPage && !routeIsOverview) {
            this.loadingService.loadingButtonEmitter.next(false);
            this.router.navigateByUrl(this.router?.url + '/page-error').then().catch();
        }
    }

    /**
     * Get message from error response.
     * @param errorResponse
     * @private
     */
    private getMessageFromError(errorResponse: ErrorResponseModel | any): string {
        const detailedMessage: string = errorResponse?.error?.errors[0]?.message;

        if (detailedMessage) {
            return detailedMessage;
        }

        return errorResponse?.message || errorResponse?.error?.message;
    }

    /**
     * Get a notification type
     * @param errorResponse
     * @private
     */
    private getNotificationType(errorResponse: any): string {
        if (errorResponse.name === 'TimeoutError' || errorResponse.message === 'Internal Server Error') {
            return 'error';
        } else if (typeof errorResponse.status === 'number') {
            return errorResponse?.error?.status || 'error';
        } else {
            return errorResponse.status;
        }
    }

    /**
     * Clean up queue request.
     * @param request
     * @private
     */
    private cleanUpRequest(request: any): void {
        this.mapRemoveItem(request.method + request.url, request.id);
        this.queueRemoveItem(request.id);
    }

    /**
     * Build the URL; if it is valid then return the url; otherwise return null.
     * @param urlPart
     * @param auth
     * @param applianceId
     */
    public buildFQUrl(urlPart: string, auth: AuthStoreModel, applianceId: string) {
        let url = 'https://' + auth.ip + ':' + auth.port;
        url += '/api/' + APIEnum.VERSION;
        if (applianceId && applianceId.length) {
            url += '/' + applianceId;
        }
        url += urlPart;

        return this.utilsService.validateUrl(url) ? url : null;
    }
}
